import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { useNavigate } from "react-router-dom";
import {
  generateToken,
  verifyToken,
  storeToken,
  getToken,
  removeToken,
} from "../lib/token";
import { verifyPassword } from "../lib/password";

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for stored token and verify it
    const token = getToken();
    if (token) {
      const userData = verifyToken(token);
      if (userData) {
        setUser(userData);
        setUserRole(userData.role);
        if (userData.category) {
          localStorage.setItem("userCategory", userData.category);
        }
      } else {
        // Token is invalid or expired, clear it
        removeToken();
        localStorage.removeItem("currentUser");
        localStorage.removeItem("userCategory");
      }
    }
    setLoading(false);
  }, []);

  const signIn = async (uniqueId, password) => {
    try {
      setLoading(true);

      // Look up the user by unique_id and verify password
      const { data: userData, error } = await supabase
        .from("users")
        .select("*")
        .eq("unique_id", uniqueId)
        .single();

      if (error || !userData) {
        return { success: false, error: "Invalid user ID or password" };
      }

      // Verify password (supports hashed and legacy plain text)
      if (!verifyPassword(password, userData.password)) {
        return { success: false, error: "Invalid user ID or password" };
      }

      // Remove password from user object before storing
      const { password: _, ...userWithoutPassword } = userData;

      // Generate token
      const token = generateToken(userData);
      storeToken(token);

      // Store user in state and localStorage
      setUser(userWithoutPassword);
      setUserRole(userData.role);
      localStorage.setItem("currentUser", JSON.stringify(userWithoutPassword));
      if (userData.category) {
        localStorage.setItem("userCategory", userData.category);
      }

      return { success: true, user: userWithoutPassword };
    } catch (error) {
      console.error("Sign in error:", error);
      return {
        success: false,
        error: error.message || "Invalid user ID or password",
      };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);

      // Clear user data and token
      setUser(null);
      setUserRole(null);
      removeToken();
      localStorage.removeItem("currentUser");
      localStorage.removeItem("userCategory");
      // Clear other user-specific localStorage keys
      localStorage.removeItem("selectedCluster");
      localStorage.removeItem("installationComplete");
      localStorage.removeItem("completionDate");
      localStorage.removeItem("selectedCustomer");

      navigate("/login");
    } catch (error) {
      console.error("Sign out error:", error);
      // Even if there's an error, we should still clear local state and navigate
      setUser(null);
      setUserRole(null);
      removeToken();
      navigate("/login");
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    userRole,
    loading,
    signIn,
    signOut,
    isAuthenticated: !!user,
    isAdmin: userRole === "super_admin" || userRole === "project_admin",
    isSupervisor: userRole === "cluster_manager",
    isStaff:
      userRole === "worker",
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
